import classnames from 'classnames';
import * as React from 'react';
import { observer } from 'mobx-react';

import { loginContainer } from 'src/login/ioc';
import { MatField } from 'src/core/components/mat-field';
import { invoiceContainer } from '../../../../ioc';

import styles from './pg-card-info.less';
import { LoginStore } from 'src/login/stores/login.store';
import { InvoiceStore } from 'src/invoice/stores/invoice.store';
import { CreditCard } from 'src/api';
import {
    Card,
    Stack,
    BodyText,
    ButtonGroup,
    Button,
    Tag,
    Form,
    Icon,
} from '@servicetitan/design-system';
import { SavedCreditCardsStore } from 'src/payment-method/stores/saved-credit-cards.store';
import { PGCardInfoFormState } from '../pg-card-info';
import { CardSelectModal } from 'src/payment-method/components/card-select-modal/card-select-modal';

interface Props {
    pgdata: PGCardInfoFormState;
    showSavePaymentInformation?: boolean | undefined;
    isEmbedded?: boolean | undefined;
    showCardholderAgreement?: boolean;
}

@observer
export class PGCardInfo extends React.Component<Props> {
    private formState: PGCardInfoFormState;
    private loginStore: LoginStore;
    private savedCreditCardsStore: SavedCreditCardsStore;
    private invoiceStore: InvoiceStore;
    private showSavePaymentInformation: boolean;
    private isEmbeded: boolean;

    constructor(props: Props) {
        super(props);
        this.formState = this.props.pgdata;
        this.loginStore = loginContainer.get(LoginStore);
        this.invoiceStore = invoiceContainer.get(InvoiceStore);
        this.savedCreditCardsStore = this.invoiceStore.getSavedCreditCardStore();
        this.showSavePaymentInformation =
            this.props.showSavePaymentInformation === undefined ? true :
                this.props.showSavePaymentInformation;
        this.isEmbeded = this.props.isEmbedded === undefined ? false :
            this.props.isEmbedded;
    }

    componentDidMount() {
        window.addEventListener('message', this.props.pgdata.iframeMessageHandler);
        if (this.formState.loggedIn) {
            this.savedCreditCardsStore.init();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.props.pgdata.iframeMessageHandler);
    }

    renderStoredPaymentCard(card: CreditCard) {
        const goToNewCard = () => {
            this.formState.setUseNewCard(true);
            this.formState.setNeedToSelectAnotherCard(false);
            this.savedCreditCardsStore.setSelectedCardToPay(undefined);
        };
        const openCardSelect = () => {
            this.formState.setNeedToSelectAnotherCard(true);
        };
        return card && this.showSavePaymentInformation ? (
            <div className={styles.cardItem}>
                <Card>
                    <Stack alignItems="center" justifyContent="space-between">
                        <Stack.Item className={styles.leftCard}>
                            <BodyText
                                size="medium"
                                className={classnames({ [styles.expired]: card.isExpired })}
                            >
                                {this.savedCreditCardsStore.getCardIssuer(card.issuer)} ending in{' '}
                                {card.number}
                            </BodyText>
                            <BodyText
                                size="medium"
                                className={classnames({ [styles.expired]: card.isExpired })}
                                subdued
                            >
                                Expires {card.expMonth.toString().padStart(2, '0')}/{card.expYear}
                            </BodyText>
                            <BodyText
                                size="medium"
                                className={classnames({ [styles.expired]: card.isExpired })}
                                subdued
                            >
                                {card.cardholderName}
                            </BodyText>
                        </Stack.Item>
                        <Stack.Item className={styles.rightCard}>
                            {card.primary && (
                                <Tag color="info" className="m-r-3 m-b-2" subtle>
                                    Primary Card
                                </Tag>
                            )}
                            {card.membership && (
                                <Tag color="info" className="m-r-3 m-b-2" subtle>
                                    {this.savedCreditCardsStore.membershipName}
                                </Tag>
                            )}
                            {card.isExpired && (
                                <Tag color="critical" className="m-r-3 m-b-2" subtle>
                                    Expired
                                </Tag>
                            )}
                            {!card.isExpired &&
                                this.savedCreditCardsStore.isExpiringSoon(
                                    card.expYear,
                                    card.expMonth
                                ) && (
                                    <Tag color="warning" className="m-r-3 m-b-2" subtle>
                                        Expiring Soon{' '}
                                    </Tag>
                                )}
                        </Stack.Item>
                    </Stack>
                </Card>
                <Stack justifyContent="flex-end">
                    <ButtonGroup className={styles.buttons}>
                        <Button primary outline fill="subtle" onClick={goToNewCard}>
                            Use New Card
                        </Button>
                        <Button primary outline onClick={openCardSelect}>
                            Change Card
                        </Button>
                    </ButtonGroup>
                </Stack>
            </div>
        ) : null;
    }

    renderUseNewCard() {
        return (
            <div className={classnames('row', styles.row, styles.firstRow)}>
                <div className={classnames('col', styles.col)}>
                    <div className="full-width">
                        <iframe
                            className={styles.iframe}
                            id="payment-gateway"
                            scrolling="no"
                            src={this.formState.desktopPaymentGatewayUrl}
                        />
                    </div>
                    <div className="full-width">
                        <MatField
                            fullWidth
                            errorColor
                            fieldState={this.formState.postalCode}
                            maxLength={10}
                            autoComplete="postal-code"
                            label="Postal Code"
                        />
                    </div>
                    {this.formState.loggedIn &&
                        this.savedCreditCardsStore.isSavedCreditCardEnabled &&
                        this.showSavePaymentInformation && (
                            <Form.Checkbox
                                className="m-y-2"
                                label={
                                    <BodyText size="small" subdued>
                                        Save payment information
                                    </BodyText>
                                }
                                checked={this.formState.needToStoreCreditCard}
                                onChange={(_, checked) => {
                                    this.formState.setNeedToStoreCreditCard(checked);
                                }}
                            />
                        )}
                    {this.formState.showLoginLink && this.showSavePaymentInformation && (
                        <div className={styles.signin}>
                            <a onClick={this.loginStore.loginWithReturn} color="primary">
                                Sign in
                            </a>
                            {' to access your saved credit card information.'}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const goToNewCard = () => {
            this.formState.setUseNewCard(true);
            this.formState.setNeedToSelectAnotherCard(false);
            this.savedCreditCardsStore.setSelectedCardToPay(undefined);
        };

        const needToDisplayBackwardIcon = () =>
            !!this.savedCreditCardsStore.savedCreditCards?.length &&
            this.formState.useNewCard &&
            this.formState.loggedIn &&
            !this.savedCreditCardsStore.selectedCardToPay;

        const goBackToNewCard = () => {
            this.formState.setUseNewCard(false);
            this.savedCreditCardsStore.setDefaultSelectedCardToPay();
        };

        return (
            <>
                <div className={this.isEmbeded ? styles.embedded : classnames('card', styles.card)}>
                    {needToDisplayBackwardIcon() && (
                        <Button fill="subtle" onClick={goBackToNewCard} className={styles.backward}>
                            <Icon name="keyboard_arrow_left" /> Back
                        </Button>
                    )}
                    {!this.isEmbeded && (<h3>Card Information</h3>)}
                    {this.formState.loggedIn &&
                        this.savedCreditCardsStore.selectedCardToPay &&
                        !this.formState.useNewCard && this.showSavePaymentInformation
                        ? this.renderStoredPaymentCard(this.savedCreditCardsStore.selectedCardToPay)
                        : this.renderUseNewCard()}
                </div>

                {this.props.showCardholderAgreement !== false && (
                    <div className={classnames('card', styles.card, styles.agreement)}>
                        <p className="gray-text">
                            I agree to pay the above amount in accordance with my cardholder
                            agreement. By clicking “Submit Payment,” you confirm that you accept the{' '}
                            <a
                                target="_blank"
                                href="https://www.servicetitan.com/terms-of-use"
                                rel="noreferrer"
                            >
                                Terms of Service
                            </a>{' '}
                            &amp;{' '}
                            <a
                                target="_blank"
                                href="https://www.servicetitan.com/privacy-policy"
                                rel="noreferrer"
                            >
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </div>
                )}
                {this.formState.needToSelectAnotherCard &&
                    this.savedCreditCardsStore.selectedCardToPay &&
                    this.showSavePaymentInformation && (
                        <CardSelectModal
                            cards={this.savedCreditCardsStore.savedCreditCards}
                            selectedCard={this.savedCreditCardsStore.selectedCardToPay}
                            setSelectedCard={this.savedCreditCardsStore.setSelectedCardToPay}
                            close={() => this.formState.setNeedToSelectAnotherCard(false)}
                            gotoNewCard={goToNewCard}
                            isOpen={this.formState.needToSelectAnotherCard}
                            getCardIssuer={this.savedCreditCardsStore.getCardIssuer}
                            gotoSavedCardPage={this.savedCreditCardsStore.gotoSavedCardPage}
                            membershipNameType={this.savedCreditCardsStore.membershipName}
                            isExpiringSoon={this.savedCreditCardsStore.isExpiringSoon}
                        />
                    )}
            </>
        );
    }
}
